import { Component } from '@theme/component';
import { debounce, isClickedOutside, lockScroll, onAnimationEnd, unlockScroll } from '@theme/utilities';
import { getScrollTop, scrollTo } from '@theme/scroll-container';

/**
 * A custom element that manages a dialog.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog – The dialog element.
 *
 * @extends Component<Refs>
 */
export class DialogComponent extends Component {
  requiredRefs = ['dialog'];

  connectedCallback() {
    super.connectedCallback();

    if (this.id === 'search-modal') {
      this.refs.dialog.addEventListener('cancel', this.#handleSearchCancel);
    }

    if (this.minWidth || this.maxWidth) {
      window.addEventListener('resize', this.#handleResize);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.id === 'search-modal') {
      this.#searchVersion += 1;
      cancelAnimationFrame(this.#searchOpenFrame);
      this.#searchOpenFrame = null;
      this.#searchClosing = false;
      this.refs.dialog.removeEventListener('cancel', this.#handleSearchCancel);
      this.removeEventListener('click', this.#handleClick);
      this.removeEventListener('keydown', this.#handleKeyDown);
      if (this.refs.dialog.open) this.refs.dialog.close();
      this.#clearSearchCloseStyles();
    }
    if (this.minWidth || this.maxWidth) {
      window.removeEventListener('resize', this.#handleResize);
    }
    unlockScroll(this.refs.dialog);
  }

  #handleResize = debounce(() => {
    const { minWidth, maxWidth } = this;

    if (!minWidth && !maxWidth) return;

    const windowWidth = window.innerWidth;
    if (windowWidth < minWidth || windowWidth > maxWidth) {
      this.closeDialog();
    }
  }, 50);

  #previousScrollY = 0;
  #searchOpenFrame = null;
  #searchClosing = false;
  #searchVersion = 0;

  // Native cancel does not bubble. Keep this listener active during the exit.
  #handleSearchCancel = (event) => {
    event.preventDefault();
    this.closeDialog();
  };

  #clearSearchCloseStyles() {
    const { dialog } = this.refs;
    dialog.classList.remove('dialog-closing');
    dialog.style.removeProperty('--cv-dialog-close-transform');
    dialog.style.removeProperty('--cv-dialog-close-opacity');
    dialog.style.removeProperty('--cv-dialog-close-shade');
  }

  #showSearchDialog() {
    const { dialog } = this.refs;
    if (dialog.open || this.#searchClosing || this.#searchOpenFrame !== null) return;

    const version = ++this.#searchVersion;
    this.#previousScrollY = getScrollTop();
    this.#searchOpenFrame = requestAnimationFrame(() => {
      this.#searchOpenFrame = null;
      if (version !== this.#searchVersion || !this.isConnected || dialog.open) return;
      this.#clearSearchCloseStyles();
      lockScroll(dialog);
      dialog.showModal();
      this.dispatchEvent(new DialogOpenEvent());
      this.addEventListener('click', this.#handleClick);
      this.addEventListener('keydown', this.#handleKeyDown);
    });
  }

  #closeSearchDialog = async () => {
    const { dialog } = this.refs;
    if (this.#searchClosing) return;

    // A close before the scheduled opening must not produce a later ghost modal.
    cancelAnimationFrame(this.#searchOpenFrame);
    this.#searchOpenFrame = null;
    const version = ++this.#searchVersion;
    if (!dialog.open) return;
    this.#searchClosing = true;

    const current = getComputedStyle(dialog);
    const shade = getComputedStyle(dialog, '::backdrop');
    dialog.style.setProperty('--cv-dialog-close-transform', current.transform);
    dialog.style.setProperty('--cv-dialog-close-opacity', current.opacity);
    dialog.style.setProperty('--cv-dialog-close-shade', shade.opacity);

    // Restart only the exit animation, from the values actually painted above.
    dialog.style.animation = 'none';
    void dialog.offsetWidth;
    dialog.classList.add('dialog-closing');
    dialog.style.animation = '';

    try {
      if (!window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        await onAnimationEnd(dialog, undefined, { subtree: false });
      }
    } finally {
      // A detached/replaced search modal must not finish a stale lifecycle.
      if (version === this.#searchVersion) {
        this.removeEventListener('click', this.#handleClick);
        this.removeEventListener('keydown', this.#handleKeyDown);
        dialog.close();
        unlockScroll(dialog);
        scrollTo({ top: this.#previousScrollY, behavior: 'instant' });
        this.#clearSearchCloseStyles();
        this.#searchClosing = false;
        this.dispatchEvent(new DialogCloseEvent());
      }
    }
  };

  /**
   * Shows the dialog.
   */
  showDialog() {
    if (this.id === 'search-modal') {
      this.#showSearchDialog();
      return;
    }
    const { dialog } = this.refs;

    if (dialog.open) return;

    this.#previousScrollY = getScrollTop();

    // Prevent layout thrashing by separating DOM reads from DOM writes
    requestAnimationFrame(() => {
      lockScroll(dialog);

      dialog.showModal();
      this.dispatchEvent(new DialogOpenEvent());

      this.addEventListener('click', this.#handleClick);
      this.addEventListener('keydown', this.#handleKeyDown);
    });
  }

  /**
   * Closes the dialog.
   */
  closeDialog = async () => {
    if (this.id === 'search-modal') {
      await this.#closeSearchDialog();
      return;
    }
    const { dialog } = this.refs;

    if (!dialog.open) return;

    this.removeEventListener('click', this.#handleClick);
    this.removeEventListener('keydown', this.#handleKeyDown);

    // Force browser to restart animation by resetting it
    // Temporarily remove any existing animation state
    dialog.style.animation = 'none';

    // Force a reflow
    void dialog.offsetWidth;

    // Now add the closing class and restore animation
    dialog.classList.add('dialog-closing');
    dialog.style.animation = '';

    await onAnimationEnd(dialog, undefined, {
      subtree: false,
    });

    unlockScroll(dialog);
    scrollTo({ top: this.#previousScrollY, behavior: 'instant' });

    dialog.close();
    dialog.classList.remove('dialog-closing');

    this.dispatchEvent(new DialogCloseEvent());
  };

  /**
   * Toggles the dialog.
   */
  toggleDialog = () => {
    if (this.id === 'search-modal' && this.#searchOpenFrame !== null) {
      this.closeDialog();
      return;
    }
    if (this.refs.dialog.open) {
      this.closeDialog();
    } else {
      this.showDialog();
    }
  };

  /**
   * Closes the dialog when the user clicks outside of it.
   *
   * @param {MouseEvent} event - The mouse event.
   */
  #handleClick(event) {
    const { dialog } = this.refs;

    if (isClickedOutside(event, dialog)) {
      this.closeDialog();
    }
  }

  /**
   * Closes the dialog when the user presses the escape key.
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyDown(event) {
    if (event.key !== 'Escape') return;

    event.preventDefault();
    this.closeDialog();
  }

  /**
   * Gets the minimum width of the dialog.
   *
   * @returns {number} The minimum width of the dialog.
   */
  get minWidth() {
    return Number(this.getAttribute('dialog-active-min-width'));
  }

  /**
   * Gets the maximum width of the dialog.
   *
   * @returns {number} The maximum width of the dialog.
   */
  get maxWidth() {
    return Number(this.getAttribute('dialog-active-max-width'));
  }
}

if (!customElements.get('dialog-component')) customElements.define('dialog-component', DialogComponent);

export class DialogOpenEvent extends CustomEvent {
  constructor() {
    super(DialogOpenEvent.eventName);
  }

  static eventName = 'dialog:open';
}

export class DialogCloseEvent extends CustomEvent {
  constructor() {
    super(DialogCloseEvent.eventName);
  }

  static eventName = 'dialog:close';
}

document.addEventListener(
  'toggle',
  (event) => {
    if (event.target instanceof HTMLDetailsElement) {
      if (event.target.hasAttribute('scroll-lock')) {
        const { open } = event.target;
        if (open) {
          lockScroll(event.target);
        } else {
          unlockScroll(event.target);
        }
      }
    }
  },
  { capture: true }
);
